import React from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Alert, Box, Button, Checkbox, Collapse, FormControlLabel, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import colors from "../../assets/theme/colors";
import { LOG_TYPES } from "../../constants/logTypeRegistry";
import { findMatchingMedication } from "./medicationPlanCandidates";
import { getImportHistoryMedicationName } from "./importHistoryMedicationReview";

const CATEGORY_OPTIONS = Object.values(LOG_TYPES).map((type) => ({ value: type.id, label: type.displayLabel }));

const formatEntryDate = (localDate) => {
  if (!localDate) return "Date needed";
  const [year, month, day] = localDate.split("-").map(Number);
  if (!year || !month || !day) return localDate;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(year, month - 1, day));
};

const AMBER = "#99600B";
const AMBER_SOFT = "#FFF7EA";
const REVIEW_INK = "#252820";
const REVIEW_MUTED = "#7B8677";
const REVIEW_LINE = "#EAEEE7";
const REVIEW_FOOTER = "#F8FAF5";
const formatUnresolvedDate = (draft) => {
  const label = String(draft.dateSourceLabel || "").trim();
  if (label) return `${label.replace(/,?\s+\d{4}\b/, "")}, year unknown`;
  return "Year unknown";
};

const replaceDateYear = (localDate, year) => {
  const parts = String(localDate || "").split("-");
  return parts.length === 3 ? [year, parts[1], parts[2]].join("-") : localDate;
};

const AssumedDateConfirmation = ({ draft, onUpdate }) => {
  const assumedYear = Number(String(draft.localDate || "").slice(0, 4));
  const previousYear = assumedYear - 1;
  const choices = [assumedYear, previousYear].filter(Number.isFinite);

  return (
    <Box sx={{ px: { xs: 2.25, sm: 3 }, py: { xs: 2.4, sm: 3 }, bgcolor: AMBER_SOFT, borderBottom: "1px solid #E8D8B9" }}>
      <Typography sx={{ color: "#8E5707", fontSize: "0.78rem", fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase" }}>
        ● &nbsp;One thing to confirm
      </Typography>
      <Typography sx={{ mt: 1.6, color: "#382F25", fontSize: { xs: "1.08rem", sm: "1.22rem" }, fontWeight: 750, lineHeight: 1.35 }}>
        The note said “{String(draft.dateSourceLabel || formatEntryDate(draft.localDate)).replace(/,?\s+\d{4}\b/, "")}” with no year. Which one?
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.1} sx={{ mt: 1.9 }}>
        {choices.map((year, index) => (
          <Button
            key={year}
            variant={index === 0 ? "contained" : "outlined"}
            onClick={() => onUpdate({ localDate: replaceDateYear(draft.localDate, year), dateConfirmed: true })}
            sx={{ minHeight: 48, px: 2.25, borderRadius: 999, textTransform: "none", fontSize: "0.93rem", fontWeight: 850, bgcolor: index === 0 ? "#9B6108" : "#FFFFFF", color: index === 0 ? "#FFFFFF" : "#6F5B3B", borderColor: "#E1D2B5", boxShadow: "none", "&:hover": { bgcolor: index === 0 ? "#7F4F08" : "#FFFDF9", borderColor: "#CDBB99", boxShadow: "none" } }}
          >
            {formatEntryDate(replaceDateYear(draft.localDate, year))}
          </Button>
        ))}
        <Button component="label" variant="outlined" sx={{ minHeight: 48, px: 2.25, borderRadius: 999, textTransform: "none", fontSize: "0.93rem", fontWeight: 850, color: "#6F5B3B", borderColor: "#E1D2B5", bgcolor: "transparent" }}>
          Pick a date
          <input hidden type="date" aria-label="Choose exact date" onChange={(event) => event.target.value && onUpdate({ localDate: event.target.value, dateConfirmed: true })} />
        </Button>
      </Stack>
    </Box>
  );
};

const getOriginalDocument = (source = {}) => {
  const metadata = source.sourceMetadata || source.metadata?.source || {};
  const attachments = metadata.documentAttachments || metadata.imageAttachments || [];
  return attachments.find((attachment) => attachment?.url) || null;
};

const EntryEditor = ({ candidate, draft, index, expanded, onExpand, onUpdate, onReviewMedication, existingMedications }) => {
  const medicationName = getImportHistoryMedicationName(candidate);
  const existingMedication = medicationName
    ? findMatchingMedication({ name: medicationName }, existingMedications)
    : null;
  const isMedicationChange = draft.category === "medication_change";

  return (
  <Box
    sx={{
      borderTop: index ? `1px solid ${colors.semantic.line}` : "none",
      bgcolor: expanded && !isMedicationChange ? "background.paper" : (draft.include ? "background.paper" : colors.semantic.sageMist),
      ...(expanded && !isMedicationChange ? {
        borderRadius: 3.5,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(46, 58, 50, 0.09)",
      } : {}),
    }}
  >
    {(!expanded || isMedicationChange) ? <Stack direction="row" alignItems="center" sx={{ px: 1.25, py: 0.55 }}>
      <Button
        aria-expanded={expanded}
        onClick={onExpand}
        sx={{ flex: 1, minWidth: 0, px: 0, py: 0.65, justifyContent: "flex-start", color: colors.semantic.ink, textTransform: "none" }}
      >
        <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
          <Typography noWrap sx={{ fontSize: "0.91rem", fontWeight: 850 }}>
            {draft.title || `Entry ${index + 1}`}
          </Typography>
          <Typography sx={{ fontSize: "0.76rem", color: draft.localDate ? colors.semantic.ink2 : "warning.main" }}>
            {formatEntryDate(draft.localDate)} · {draft.category || "Uncategorized"}{draft.dateYearAssumed && !draft.dateConfirmed ? " · Year needs confirmation" : ""}
          </Typography>
        </Box>
        <ExpandMoreRoundedIcon sx={{ ml: 0.5, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 180ms ease" }} />
      </Button>
    </Stack> : null}

    <Collapse in={expanded} unmountOnExit>
      {!isMedicationChange && draft.dateYearAssumed && !draft.dateConfirmed ? <AssumedDateConfirmation draft={draft} onUpdate={onUpdate} /> : null}
      <Stack spacing={0} sx={{ px: { xs: 2.25, sm: 3 }, py: { xs: 1.7, sm: 2.1 }, bgcolor: "background.paper" }}>
        {candidate?.possibleOverlap ? <Alert severity="warning">Possible overlap with an entry from {candidate.possibleOverlap.fileName}. Review both before saving.</Alert> : null}
        {!draft.localDate && !draft.include ? <Alert severity="info">Add a date, then select Include to save this entry.</Alert> : null}
        {isMedicationChange && draft.dateYearAssumed && !draft.dateConfirmed ? (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => onUpdate({ dateConfirmed: true })}>Confirm date</Button>}>Confirm the assumed year before adding this medication change to the Timeline.</Alert>
        ) : null}
        {[
          { label: "Title", field: <TextField variant="standard" value={draft.title} onChange={(event) => onUpdate({ title: event.target.value })} fullWidth inputProps={{ "aria-label": "Title" }} InputProps={{ disableUnderline: true }} /> },
          { label: "Details", field: <TextField variant="standard" value={draft.text} onChange={(event) => onUpdate({ text: event.target.value })} fullWidth multiline minRows={2} inputProps={{ "aria-label": "Care details" }} InputProps={{ disableUnderline: true }} /> },
          { label: "Date", field: !isMedicationChange && draft.dateYearAssumed && !draft.dateConfirmed ? <Typography sx={{ display: "inline-flex", px: 1.2, py: 0.65, borderRadius: 1.5, bgcolor: "#FFF0D7", color: AMBER, fontWeight: 850 }}>{formatUnresolvedDate(draft)}</Typography> : <TextField variant="standard" type="date" value={draft.localDate} onChange={(event) => onUpdate({ localDate: event.target.value })} fullWidth inputProps={{ "aria-label": "Date" }} InputProps={{ disableUnderline: true }} /> },
          { label: "Time", field: <TextField variant="standard" type="time" value={draft.localTime} onChange={(event) => onUpdate({ localTime: event.target.value })} fullWidth inputProps={{ "aria-label": "Time" }} InputProps={{ disableUnderline: true }} /> },
          { label: "Category", field: <TextField select variant="standard" value={draft.category} onChange={(event) => onUpdate({ category: event.target.value })} fullWidth SelectProps={{ inputProps: { "aria-label": "Category" } }} InputProps={{ disableUnderline: true }}>{CATEGORY_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField> },
        ].map((row, rowIndex) => (
          <Box key={row.label} sx={{ display: "grid", gridTemplateColumns: { xs: "86px minmax(0, 1fr)", sm: "150px minmax(0, 1fr)" }, gap: { xs: 1.5, sm: 2 }, alignItems: "start", minHeight: row.label === "Details" ? 90 : 64, py: 1.55, borderTop: rowIndex ? `1px solid ${REVIEW_LINE}` : "none" }}>
            <Typography sx={{ color: REVIEW_MUTED, fontSize: "0.92rem", fontWeight: 800 }}>{row.label}</Typography>
            <Box sx={{ minWidth: 0, "& .MuiInputBase-root": { color: REVIEW_INK, fontSize: "1rem", fontWeight: 650 } }}>{row.field}</Box>
          </Box>
        ))}
        {draft.category === "medication_change" && medicationName && onReviewMedication ? (
          <Box sx={{ mt: 1.25, border: `1px solid ${alpha(colors.brand.deep, 0.2)}`, borderRadius: 2.25, overflow: "hidden" }}>
            <Box sx={{ p: 1.4, bgcolor: alpha(colors.brand.deep, 0.055) }}>
              <Typography sx={{ color: colors.brand.deep, fontSize: "0.74rem", fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase" }}>This entry does two things</Typography>
              <Typography sx={{ mt: 0.4, color: colors.semantic.ink2, fontSize: "0.86rem", lineHeight: 1.45 }}>Medication changes belong on the Timeline and may also update the medication list.</Typography>
            </Box>
            <Stack spacing={0.45} sx={{ p: 1.4, borderTop: `1px solid ${colors.semantic.line}` }}>
              <Typography sx={{ fontWeight: 900 }}>1&nbsp;&nbsp; Adds an entry to the Timeline</Typography>
              <Typography sx={{ pl: 3, color: colors.semantic.ink2, fontSize: "0.82rem" }}>{draft.localDate ? `Dated ${formatEntryDate(draft.localDate)} under Medication change.` : "Add a date before including this Timeline entry."}</Typography>
            </Stack>
            <Stack spacing={0.45} sx={{ p: 1.4, borderTop: `1px solid ${colors.semantic.line}`, bgcolor: "#FFFBF3" }}>
              <Typography sx={{ fontWeight: 900 }}>2&nbsp;&nbsp; {existingMedication ? `Reviews ${medicationName} in the medication list` : `Adds ${medicationName} to the medication list`}</Typography>
              <Button onClick={() => onReviewMedication(candidate, draft)} sx={{ alignSelf: "flex-start", ml: 2, px: 0.5, textTransform: "none", fontWeight: 900 }}>{existingMedication ? `Review ${medicationName}` : `Set up ${medicationName}`} →</Button>
            </Stack>
          </Box>
        ) : null}
        {isMedicationChange && draft.sourceText ? (
          <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: colors.semantic.sageMist }}>
            <Typography sx={{ mb: 0.4, fontSize: "0.7rem", fontWeight: 850, color: colors.semantic.ink2, textTransform: "uppercase", letterSpacing: "0.06em" }}>Where this came from</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", lineHeight: 1.55 }}>{draft.sourceText}</Typography>
          </Box>
        ) : null}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minHeight: 72, px: { xs: 2, sm: 2.75 }, py: 1.2, bgcolor: REVIEW_FOOTER, borderTop: `1px solid ${REVIEW_LINE}` }}>
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Checkbox checked={draft.include} disabled={draft.dateYearAssumed && !draft.dateConfirmed} onChange={(event) => onUpdate({ include: event.target.checked })} sx={{ color: "#D6DDD1", "&.Mui-checked": { color: colors.brand.deep } }} />}
          label={<Typography sx={{ color: draft.dateYearAssumed && !draft.dateConfirmed ? alpha(colors.semantic.ink2, 0.58) : colors.semantic.ink, fontSize: "0.95rem", fontWeight: 850 }}>Add to timeline</Typography>}
        />
        {draft.dateYearAssumed && !draft.dateConfirmed ? <Typography sx={{ color: AMBER, fontWeight: 850, fontSize: "0.82rem" }}>Confirm the date first</Typography> : null}
      </Stack>
    </Collapse>
  </Box>
  );
};

const ImportHistoryReviewGroup = ({ group, drafts, updateDraft, onSelectedCandidateChange, onReviewMedication, existingMedications = [] }) => {
  const [expandedEntryKey, setExpandedEntryKey] = React.useState(group.candidates[0]?.key || "");
  const originalDocument = getOriginalDocument(group.source);
  const includedCount = drafts.filter((draft) => draft.include).length;
  const datedCount = drafts.filter((draft) => draft.localDate).length;

  React.useEffect(() => setExpandedEntryKey(group.candidates[0]?.key || ""), [group.sourceNoteId, group.candidates]);
  React.useEffect(() => {
    const selectedCandidate = group.candidates.find((candidate) => candidate.key === expandedEntryKey);
    if (selectedCandidate) {
      onSelectedCandidateChange?.({
        key: selectedCandidate.key,
        sourceText: selectedCandidate.suggestedRecord?.sourceText || selectedCandidate.suggestedRecord?.record?.sourceText || "",
      });
    }
  }, [expandedEntryKey, group.candidates, onSelectedCandidateChange]);

  return (
    <Box sx={{ border: `1px solid ${colors.semantic.line2}`, borderRadius: 2.5, overflow: "hidden", bgcolor: "background.paper" }}>
      <Stack direction="row" alignItems="center" sx={{ bgcolor: alpha(colors.semantic.sageMist, 0.7) }}>
        <Box sx={{ minWidth: 0, px: 1.5, py: 1.2, flex: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
            <Typography noWrap sx={{ fontWeight: 900, fontSize: "0.92rem" }}>{group.fileName}</Typography>
            <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.76rem" }}>
              {group.candidates.length} {group.candidates.length === 1 ? "entry" : "entries"} · {includedCount} included
            </Typography>
          </Box>
        </Box>
        {originalDocument?.url ? <Button aria-label="View original document" component="a" href={originalDocument.url} target="_blank" rel="noreferrer" size="small" sx={{ flexShrink: 0, mr: 0.75, textTransform: "none" }}>Original</Button> : null}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.8, borderTop: `1px solid ${colors.semantic.line}` }}>
        <Typography sx={{ fontSize: "0.78rem", color: colors.semantic.ink2 }}>{includedCount} of {group.candidates.length} will be saved</Typography>
        <Button size="small" onClick={() => drafts.forEach((draft) => updateDraft(draft.key, { include: Boolean(draft.localDate) && (!draft.dateYearAssumed || draft.dateConfirmed) }))} sx={{ textTransform: "none", fontWeight: 800 }}>
          Include all {datedCount} dated
        </Button>
      </Stack>
      <Box>
        {drafts.map((draft, index) => (
          <EntryEditor
            key={draft.key}
            candidate={group.candidates.find((item) => item.key === draft.key)}
            draft={draft}
            index={index}
            expanded={expandedEntryKey === draft.key}
            onExpand={() => setExpandedEntryKey((current) => (current === draft.key ? "" : draft.key))}
            onUpdate={(patch) => updateDraft(draft.key, patch)}
            onReviewMedication={onReviewMedication}
            existingMedications={existingMedications}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ImportHistoryReviewGroup;
